import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, RefreshCw, Loader2, TrendingUp, Package, Truck, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(v) {
  return v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '--';
}

function pct(a, b) {
  if (!b) return '--';
  return `${Math.round((a / b) * 100)}%`;
}

function MiniBar({ value, max, color = 'accent' }) {
  const pctW = max > 0 ? Math.round((value / max) * 100) : 0;
  const cls = color === 'green' ? 'bg-green-500' : color === 'blue' ? 'bg-blue-500' : 'bg-accent';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${cls}`} style={{ width: `${pctW}%` }} />
      </div>
      <span className="text-xs w-6 text-right">{value}</span>
    </div>
  );
}

export default function LoadPerformancePage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [periodsBack, setPeriodsBack] = useState('12');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, periods_back: periodsBack });
      const res = await window.fetch(`${API_URL}/api/admin/reports/load-performance?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, period, periodsBack]);

  useEffect(() => { fetch(); }, [fetch]);

  const rows = data?.data || [];
  const maxCreated = Math.max(...rows.map((r) => r.created), 1);
  const maxDeposits = Math.max(...rows.map((r) => r.deposits), 1);
  const maxDelivered = Math.max(...rows.map((r) => r.delivered), 1);

  const totals = rows.reduce(
    (acc, r) => ({
      created:  acc.created  + r.created,
      deposits: acc.deposits + r.deposits,
      delivered: acc.delivered + r.delivered,
      revenue:  acc.revenue  + r.total_revenue,
    }),
    { created: 0, deposits: 0, delivered: 0, revenue: 0 }
  );

  return (
    <div className="space-y-6" data-testid="load-performance-report">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-accent" strokeWidth={1.5} />
            Load Performance
          </h1>
          <p className="text-muted-foreground text-sm">
            {period === 'weekly' ? 'Weekly' : 'Monthly'} breakdown of load activity and revenue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-transparent border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={periodsBack} onValueChange={setPeriodsBack}>
            <SelectTrigger className="w-36 bg-transparent border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {period === 'weekly'
                ? [['4','Last 4 weeks'],['8','Last 8 weeks'],['12','Last 12 weeks'],['26','Last 26 weeks']].map(([v,l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))
                : [['3','Last 3 months'],['6','Last 6 months'],['9','Last 9 months'],['12','Last 12 months']].map(([v,l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))
              }
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetch} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Loads',    value: totals.created,   icon: Package,    color: '' },
          { label: 'Total Deposits', value: totals.deposits,  icon: DollarSign, color: 'green' },
          { label: 'Delivered',      value: totals.delivered, icon: Truck,      color: 'green' },
          { label: 'Total Revenue',  value: fmt(totals.revenue), icon: TrendingUp, color: 'green' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="bg-card border-white/5">
            <CardContent className="p-5 flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                <p className={`text-2xl font-outfit font-semibold ${color === 'green' ? 'text-green-500' : ''}`}>
                  {value}
                </p>
              </div>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color === 'green' ? 'bg-green-500/10' : 'bg-accent/10'}`}>
                <Icon className={`h-4 w-4 ${color === 'green' ? 'text-green-500' : 'text-accent'}`} strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Table */}
      <Card className="bg-card border-white/5">
        <CardHeader>
          <CardTitle className="font-outfit text-base">
            {period === 'weekly' ? 'Weekly' : 'Monthly'} Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">No data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-xs">
                    <th className="text-left py-3 px-4">Period</th>
                    <th className="py-3 px-4 text-left min-w-[140px]">Loads Created</th>
                    <th className="py-3 px-4 text-left min-w-[140px]">Deposits</th>
                    <th className="text-right py-3 px-4">Dispatched</th>
                    <th className="py-3 px-4 text-left min-w-[140px]">Delivered</th>
                    <th className="text-right py-3 px-4">Conv. Rate</th>
                    <th className="text-right py-3 px-4">Revenue</th>
                    <th className="text-right py-3 px-4">Avg Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const convRate = row.created > 0 ? Math.round((row.deposits / row.created) * 100) : 0;
                    return (
                      <tr key={row.period} className="border-b border-white/5 hover:bg-muted/20">
                        <td className="py-3 px-4 font-medium whitespace-nowrap">{row.label}</td>
                        <td className="py-3 px-4">
                          <MiniBar value={row.created} max={maxCreated} />
                        </td>
                        <td className="py-3 px-4">
                          <MiniBar value={row.deposits} max={maxDeposits} color="green" />
                        </td>
                        <td className="py-3 px-4 text-right">{row.dispatched}</td>
                        <td className="py-3 px-4">
                          <MiniBar value={row.delivered} max={maxDelivered} color="blue" />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Badge className={`text-xs px-2 py-0.5 ${convRate >= 20 ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
                            {convRate}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">{fmt(row.total_revenue)}</td>
                        <td className={`py-3 px-4 text-right ${row.avg_margin >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {fmt(row.avg_margin)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/20 bg-muted/10 text-sm font-semibold">
                    <td className="py-3 px-4 text-muted-foreground">Total</td>
                    <td className="py-3 px-4">
                      <span className="text-xs">{totals.created}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs">{totals.deposits}</span>
                    </td>
                    <td className="py-3 px-4 text-right">--</td>
                    <td className="py-3 px-4">
                      <span className="text-xs">{totals.delivered}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {pct(totals.deposits, totals.created)}
                    </td>
                    <td className="py-3 px-4 text-right">{fmt(totals.revenue)}</td>
                    <td className="py-3 px-4 text-right">--</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

